/**
 * bootstrap — boot-time wiring for the Scene Designer Toolbox.
 *
 * Phase 13 P0 (June 2026)
 * =======================
 *
 * What this module owns
 * ---------------------
 *
 *   1. **Feature-flag gate.** The shell only mounts when
 *      `flowgenix.designer.shell_v2` is on. Until P4 cutover, the
 *      flag defaults OFF in production; QA / dev environments turn
 *      it on via an env var or a localStorage override.
 *
 *   2. **Registry initialization.** Imports the per-phase tool
 *      modules and registers them. P0 ships with ZERO tools — the
 *      registry is intentionally empty so we can prove the shell
 *      mounts, the context exposes the full API, and the registry's
 *      filtering pipeline works end-to-end before any tool extraction
 *      lands. P1 / P2 / P3 land tools by appending to the import
 *      chain below.
 *
 *   3. **Feature-flag resolver injection.** Wires the registry's
 *      `setFeatureFlagResolver` so per-tool feature-flag fields
 *      consult the same mechanism as the shell-level flag.
 *
 * Boot ordering
 * -------------
 *
 *     // Once per app boot, BEFORE any DesignerProvider / DesignerShell
 *     // mounts.
 *     import { bootstrapDesignerToolbox } from './designer/bootstrap';
 *     bootstrapDesignerToolbox();
 *
 * Idempotent — calling it twice is a no-op (the registry guards
 * against duplicate ids; flag wiring is reentrant).
 *
 * Forward-compat
 * --------------
 *
 * The boot function accepts a `config` arg so future apps can
 * override the flag-resolver, supply per-tenant tool packs, or
 * disable specific tool ids without forking this file.
 */

import {
  registry,
  setFeatureFlagResolver,
} from './DesignerToolRegistry';

// ─── P1 platform tools ──────────────────────────────────────────────
//
// Each module exports either a single DesignerTool instance (default
// export) or an array of related tools. The bootstrap chains them
// into a single `PLATFORM_TOOLS` array so registration is one
// `registerAll(PLATFORM_TOOLS)` call. Adding a new platform tool
// means appending its module to the spread below — no other code
// changes the registration surface.
//
// IMPORTANT: this module imports ZERO domain-specific tools. Domain
// packs (football, basketball, etc.) register their tools at the
// app-boot call-site via the `extraTools` config arg (see Phase 13
// P5 polish — the framework stays domain-agnostic so a tenant
// without football still ships a clean bundle).
import metadataForm    from './tools/MetadataForm';
import navigationTools from './tools/NavigationTools';
import inspectorTools  from './tools/InspectorTools';
import mcqEditor       from './tools/MCQEditor';
import telestrationToolbar from './tools/TelestrationToolbar';
import whiteboardStamps    from './tools/WhiteboardStamps';
import timelineStrip   from './tools/TimelineStrip';
import videoTools      from './tools/VideoTools';
import slideTools      from './tools/SlideTools';
import quizDesignerTools from './tools/QuizDesignerTools';
import contentEditorTools from './tools/ContentEditorTools';
import attachmentUploader from './tools/AttachmentUploader';
import codeLabDesigner from './tools/CodeLabDesigner';
import capstoneDesigner from './tools/CapstoneDesigner';

/** The canonical platform tool list. Order is informational —
 *  the registry's `weight` field is what determines on-screen
 *  rendering order. P1 platform tools weights are 5..75; P3 quiz
 *  tools live in the same bucket (key by category, not weight,
 *  for slot-internal ordering). Domain tools (P2) MUST use 100+
 *  so they always sort below platform tools in their slot. */
const PLATFORM_TOOLS = [
  metadataForm,
  ...navigationTools,
  ...inspectorTools,
  mcqEditor,
  telestrationToolbar,
  whiteboardStamps,
  timelineStrip,
  ...videoTools,
  ...slideTools,
  ...quizDesignerTools,         // P3
  ...contentEditorTools,        // P5 — prompt_canvas, ai_lab
  attachmentUploader,           // downloadable resources (all scene types)
  codeLabDesigner,              // P1 — code_lab scene authoring
  capstoneDesigner,             // P1 — capstone scene authoring
];

// ────────────────────────────────────────────────────────────────────
// Feature-flag mechanics.
// ────────────────────────────────────────────────────────────────────

/** The shell-level flag. Default ON since P5 cutover (Jun 2026). */
export const SHELL_V2_FLAG = 'flowgenix.designer.shell_v2';

/**
 * Single source of truth for "is this flag on right now". Reads, in
 * order:
 *
 *   1. URL query string `?ff=<name>=on|off` (sticky for the session
 *      via sessionStorage; useful for QA without persistent state).
 *   2. localStorage[`flowgenix.flag.<name>`] (the persistent
 *      per-user override the shell user-prefs surfaces).
 *   3. Build-time default from `process.env.REACT_APP_FF_<NAME_UPPER>`.
 *      Vite `define` writes this; absence means "use module default".
 *   4. The hard-coded module default — keep one entry per known
 *      flag.
 *
 * @param {string} flag
 * @returns {boolean}
 */
export function isFlagOn(flag) {
  if (!flag) return true;

  if (typeof window === 'undefined') {
    return _hardDefault(flag);
  }

  // 1. URL ?ff=name=on
  try {
    const sticky = window.sessionStorage?.getItem(`flowgenix.flag.session.${flag}`);
    if (sticky === '1' || sticky === 'on') return true;
    if (sticky === '0' || sticky === 'off') return false;
    const params = new URLSearchParams(window.location.search);
    const ff = params.getAll('ff');
    for (const entry of ff) {
      const [name, val] = entry.split('=');
      if (name === flag) {
        const on = val === 'on' || val === '1' || val === 'true';
        try {
          window.sessionStorage?.setItem(
            `flowgenix.flag.session.${flag}`, on ? '1' : '0',
          );
        } catch (_) { /* private mode */ }
        return on;
      }
    }
  } catch (_) { /* SSR / locked-down browser */ }

  // 2. localStorage
  try {
    const v = window.localStorage?.getItem(`flowgenix.flag.${flag}`);
    if (v === '1' || v === 'true' || v === 'on') return true;
    if (v === '0' || v === 'false' || v === 'off') return false;
  } catch (_) { /* private mode */ }

  // 3. Build-time default. Convention:
  //   flowgenix.designer.shell_v2 → REACT_APP_FF_FLOWGENIX_DESIGNER_SHELL_V2
  try {
    const envKey = 'REACT_APP_FF_' + flag.toUpperCase().replace(/[^A-Z0-9]+/g, '_');
    const v = process.env[envKey];
    if (v === '1' || v === 'true' || v === 'on') return true;
    if (v === '0' || v === 'false' || v === 'off') return false;
  } catch (_) { /* SSR */ }

  return _hardDefault(flag);
}

/** Hard-coded module defaults. Keep one entry per known flag. */
function _hardDefault(flag) {
  switch (flag) {
    case SHELL_V2_FLAG:
      return true;        // Phase 13 P5 cutover — default ON (Jun 2026)
    default:
      return false;
  }
}

/**
 * Test/dev helper: force a flag value at runtime. Persists to
 * localStorage so the override survives reloads. Not exposed in
 * the production UI; used by the platform-admin developer tools.
 *
 * @param {string} flag
 * @param {boolean} on
 */
export function setFlag(flag, on) {
  if (typeof window === 'undefined') return;
  try {
    if (on) {
      window.localStorage.setItem(`flowgenix.flag.${flag}`, '1');
    } else {
      window.localStorage.setItem(`flowgenix.flag.${flag}`, '0');
    }
  } catch (_) { /* private mode */ }
}

// ────────────────────────────────────────────────────────────────────
// Boot.
// ────────────────────────────────────────────────────────────────────

let _bootedOnce = false;

/**
 * Wire the registry's feature-flag resolver and register every
 * platform tool. Call exactly once per app boot.
 *
 * @param {Object} [config]
 * @param {(flag: string) => boolean} [config.flagResolver]
 *   Override the default resolver; useful for tests.
 * @param {Iterable<import('./DesignerTool').DesignerTool>} [config.extraTools]
 *   Domain packs / tenant-specific bundles can pass their own tools
 *   here. They register AFTER the platform tools, so a tenant tool
 *   colliding with a platform id throws (as desired).
 */
export function bootstrapDesignerToolbox(config = {}) {
  if (_bootedOnce) {
    // Idempotent: subsequent calls re-wire the resolver (so tests
    // can swap it) but skip re-registration.
    if (config.flagResolver) setFeatureFlagResolver(config.flagResolver);
    return registry;
  }

  setFeatureFlagResolver(config.flagResolver || isFlagOn);

  // ─── Platform tools (P1) ──────────────────────────────────────────
  // Register every P1 platform tool. The registry's filter pipeline
  // (feature_flag → RBAC → applies_to) decides which actually mount
  // for a given scene. P2 / P3 / domain packs append to this list
  // by passing `extraTools`.
  registry.registerAll(PLATFORM_TOOLS);

  // ─── Caller-supplied extras ───────────────────────────────────────
  if (config.extraTools) {
    registry.registerAll(config.extraTools);
  }

  _bootedOnce = true;
  return registry;
}

/**
 * Convenience predicate the legacy designer pages use to decide
 * whether to mount the new shell or the old inline-tools layout.
 *
 * Pages should look like:
 *
 *     return isShellV2On()
 *       ? <DesignerShell canvasSlot={<MyCanvas />} />
 *       : <LegacyMyCanvasPage />;
 */
export function isShellV2On() {
  return isFlagOn(SHELL_V2_FLAG);
}

/** Test-only: reset the boot guard so unit tests can re-bootstrap. */
export function _resetBootForTests() {
  _bootedOnce = false;
  registry.reset();
}
